import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ProductIdentifier from '@ikea-ux/product-identifier';

import Loader from 'components/Loader';
import Text from 'components/Text';

import TotalPrice from './components/TotalPrice';
import SimpleTotalPrice from './components/SimpleTotalPrice';
import ProductControls from './components/ProductControls';
import ProductStock from './components/ProductStock';
import ProductPackages from './components/ProductPackages';
import ProductPartTrigger from './components/ProductPartTrigger';
import MiniProductPackages from './components/MiniProductPackages';
import ProductPlaceholder from './components/ProductPlaceholder';
import Efficiency from './components/Efficiency';
import EcoFee from './components/EcoFee';

import { getText } from 'helpers/i18n';

// Styles
import './product.styles.scss';
import '@ikea-ux/product-identifier/dist/style.css';

export default class Product extends Component {
    componentDidMount() {
        const { getProductData, getStock, data, store} = this.props;

        if (!data?.productName) {
            getProductData(data.articleNumber, data.articleType, data.quantity);
        }

        // Get product stock if store is selected, currently viewing shopping list and stock has not been previously retrieved.
        if (store !== '0' && !data.stockAvailability && data.articleNumber) {
            getStock(data, store);
        }
    }

    // Update the component only if loading flag is not the same, the product data has not been retrieved,
    // stock finishes loading, the store changes, the list tab changes, or a discount code is changed.
    shouldComponentUpdate(nextProps) {
        const { data, isLoading, listView, store, hasDiscountCode } = this.props;

        if (isLoading !== nextProps.isLoading ) {
            return true;
        }
        if (typeof data.productName === 'undefined' && nextProps.data.productName) {
            return true;
        }
        if (typeof data.stockAvailability === 'undefined' && nextProps.data.stockAvailability) {
            return true;
        }
        if (store !== nextProps.store) {
            return true;
        }
        if (listView !== nextProps.listView) {
            return true;
        }
        if (hasDiscountCode !== nextProps.hasDiscountCode) {
            return true;
        }

        return false;
    }

    componentDidUpdate(prevProps) {
        const { getStock, data, store, getProductData } = this.props;

        if (store !== '0' && prevProps.store !== store && data.articleNumber) {
            getStock(data, store);
        }

        if (!data.productName) {
            getProductData(data.articleNumber, data.articleType, data.quantity);
        }
    }

    renderProductMeasurements() {
        const { data } = this.props;

        const showMetric = window.Checkout.settings.show_metric_product_measurements;
        const showImperial = window.Checkout.settings.show_imperial_product_measurements;

        if (!data.measurements) return null;

        if (showMetric && !showImperial) {
            return (
                <li className="product__description-product-measurements">
                    {data.measurements.metric}
                </li>
            );
        }

        if (!showMetric && showImperial) {
            return (
                <li className="product__description-product-measurements">
                    {data.measurements.imperial}
                </li>
            );
        }

        if (showMetric && showImperial) {
            return (
                <li className="product__description-product-measurements">
                    {data.measurements.metric}
                    {data.measurements.imperial !== '' ? ' (' + data.measurements.imperial + ')' : ''}
                </li>
            );
        }

        // …else neither showMetric nor showImperial
        return null;
    }

    render() {
        const { data, isLoading, listView, hasDiscountCode, excludeVAT } = this.props;
        // Product data is not loaded yet, show a placeholder until it's retrieved
        if (typeof data.productName === 'undefined') {
            return (
                <div className="product">
                    <ProductPlaceholder />
                </div>
            );
        }

        return (
            <div itemScope itemType="http://schema.org/Product" className="product">
                <div className={`product__contents ${isLoading ? 'product__blur' : ''}`}>
                    <div className="product__image-container">
                        <img
                            src={'https://www.ikea.com' + data.s3Image}
                            alt={`IKEA ${data.productName} ${data.productType}`}
                            aria-describedby={'aria_product_description_' + data.articleNumber}
                            itemProp="image"
                        />
                    </div>
                    <div className="product__description" id={'aria_product_description_' + data.articleNumber}>
                        {data.isNew && window.Checkout.settings.show_new_products_label &&
                            <p className="product__new"><Text textKey="product_new_label_text" /></p>
                        }
                        {data.familyPrice && <span className="product__family_badge" style={{}}>{getText('price_package_family_text')}</span>}
                        <h2 itemProp="name" className="product__name">
                            <a href={window.Checkout.settings.pip_url + (data.articleType.toLowerCase() === 'spr' ? 's' : '') + data.articleNumber}>
                                {data.productName}
                            </a>
                        </h2>
                        <ul
                            itemProp="description"
                            className="product__description-list"
                        >
                            {data.productType &&
                                <li className="product_description-type">{data.productType}</li>
                            }
                            {data.designText &&
                                <li className="product__description-design-text">{data.designText}</li>
                            }
                            { this.renderProductMeasurements() }
                            <Efficiency value={data.efficiency}/>
                            {data.articleNumberFormatted &&
                                <li className="product_description-article-number">
                                    {data.articleNumberFormatted}
                                    {data.cabinetNumber &&
                                        <ProductIdentifier type="cabinet-code" productIdPrefix={data.cabinetNumber.split(' ')[0]} number={data.cabinetNumber.split(' ')[1]} />
                                    }
                                </li>
                            }
                        </ul>
                        {window.Checkout.settings.show_mini_product_parts && listView !== 'availableinstore' &&
                            <>
                                <ProductPartTrigger
                                    parentArticle={data.articleNumber}
                                />
                                <MiniProductPackages
                                    productId={data.articleNumber}
                                />
                            </>
                        }
                    </div>
                    <div className="product__prices">
                        { data.familyPrice ?
                            <>
                                <TotalPrice
                                    article={data.articleNumber}
                                    unitPrice
                                    type="family"
                                />
                                { hasDiscountCode ?
                                    <>
                                        <SimpleTotalPrice
                                            article={data.articleNumber}
                                            unitPrice
                                            type="regularfamilyprice"
                                            typeText="price_package_family_text_before_discount"
                                            separator={<br/>}
                                        />
                                        <SimpleTotalPrice
                                            article={data.articleNumber}
                                            unitPrice
                                            type="discount"
                                            typeText="price_package_regular_text_after_discount"
                                            hideValidToDateTime
                                            separator={<br/>}
                                        />
                                        <SimpleTotalPrice
                                            article={data.articleNumber}
                                            unitPrice
                                            type="regular"
                                            typeText="price_package_regular_text_before_discount"
                                            hideValidToDateTime
                                            separator={<br/>}
                                        />
                                    </> :
                                    <SimpleTotalPrice
                                        article={data.articleNumber}
                                        unitPrice
                                        type="regular"
                                        typeText="price_package_regular_text"
                                        separator=": "
                                    />
                                }

                            </> :
                            <>
                                { hasDiscountCode ?
                                    <>
                                        <TotalPrice
                                            article={data.articleNumber}
                                            unitPrice
                                            type="discount"
                                            typeText=""
                                        />
                                        <SimpleTotalPrice
                                            article={data.articleNumber}
                                            unitPrice
                                            type="regular"
                                            typeText="price_package_regular_text"
                                            separator=": "
                                        />
                                    </> :
                                    <TotalPrice
                                        article={data.articleNumber}
                                        unitPrice
                                        type="regular"
                                        typeText=""
                                    />
                                }
                            </>
                        }
                        { data.ecoFee &&
                            <EcoFee
                                regularPrice={excludeVAT ? data.regularPrice.exclTax : data.regularPrice.inclTax}
                                fees={data.ecoFee}/>
                        }
                        <div className="print-only">
                            <Text textKey="quantity" />: {data.quantity}
                        </div>
                    </div>
                    {listView !== 'buyonline' &&
                        <ProductStock
                            parentArticle={data.articleNumber}
                        />
                    }
                    <div className="product__controls">
                        <ProductControls
                            article={data.articleNumber.toString()}
                            className={isLoading ? 'product__blur' : ''}
                        />
                    </div>
                    {listView !== 'buyonline' &&
                        <div className="product__packages">
                            <ProductPackages
                                productId={data.articleNumber}
                            />
                        </div>
                    }
                </div>
                {isLoading &&
                    <div className="product__loading">
                        <Loader />
                    </div>
                }
            </div>
        );
    }
}

Product.propTypes = {
    productId: PropTypes.any.isRequired,
    data: PropTypes.object,
    selectedList: PropTypes.any,
    store: PropTypes.string.isRequired,
    getProductData: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    getStock: PropTypes.func.isRequired,
    listView: PropTypes.string,
    hasDiscountCode: PropTypes.bool,
    excludeVAT: PropTypes.bool,
};
